import React from 'react';
import {
  LuAccessibility as AccessibilityIcon,
  LuCalendarDays as PermContactCalendarIcon,
  LuCog as PrecisionManufacturingIcon,
  LuFileText as SummarizeIcon,
  LuHardHat as EngineeringIcon,
  LuHelpCircle as LiveHelpIcon,
  LuLogOut as LogoutIcon,
  LuMap as MapIcon,
  LuMapPin as PlaceIcon,
  LuMegaphone as CampaignIcon,
  LuMessageCircle as QuestionAnswerIcon,
  LuNavigation as GpsFixedIcon,
  LuRadio as SensorsIcon,
  LuShield as AdminPanelSettingsIcon,
  LuTrendingUp as InsightsIcon,
  LuTrendingUp as TimelineIcon,
  LuUser as AccountBoxIcon,
  LuUser as PersonIcon,
  LuUserPlus as PersonAddIcon,
  LuUsers as PeopleAltIcon
} from 'react-icons/lu';

import { UserAPI } from '../../features/users/services/user-api';
import { AppRoute } from './types';

// Lazy load all components to prevent unnecessary instantiation
const Accesibility = React.lazy(() => import('../../features/accesibility'));
const AdminMenu = React.lazy(() => import('../../features/adminMenu'));
const Logout = React.lazy(() => import('../../features/auth/pages/logout'));
const CreateNotice = React.lazy(() => import('../../features/createNotice'));
const CreateUser = React.lazy(() => import('../../features/createUser'));
const FAQ = React.lazy(() => import('../../features/faq'));
const HeatMap = React.lazy(() => import('../../features/heatMap'));
const HelpMenu = React.lazy(() => import('../../features/helpMenu'));
const LiveMap = React.lazy(() => import('../../features/liveMap'));
const Machine = React.lazy(() => import('../../features/machine'));
const Machines = React.lazy(() => import('../../features/machines'));
const Notices = React.lazy(() => import('../../features/notices'));
const Profile = React.lazy(() => import('../../features/profile'));
const ReportDetail = React.lazy(() => import('../../features/report'));
const Reports = React.lazy(() => import('../../features/reports'));
const ReportsMenu = React.lazy(() => import('../../features/statisticsMenu'));
const Tracking = React.lazy(() => import('../../features/trackingMenu'));
const User = React.lazy(() => import('../../features/user'));
const MyUserMenu = React.lazy(() => import('../../features/userMenu'));
const Users = React.lazy(() => import('../../features/users'));
const WorkerDetail = React.lazy(() => import('../../features/worker'));
const Workers = React.lazy(() => import('../../features/workers'));
const ZoneDetail = React.lazy(() => import('../../features/zone'));
const Zones = React.lazy(() => import('../../features/zones'));

// Wrapper component for lazy loaded components with Suspense
const LazyWrapper = ({ Component }: { Component: React.LazyExoticComponent<React.ComponentType<any>> }) => (
  <React.Suspense fallback={<div>Cargando...</div>}>
    <Component />
  </React.Suspense>
);

export const appRoutesLazy: AppRoute[] = [
  {
    title: 'Monitoreo',
    Icon: GpsFixedIcon,
    path: 'monitoreo',
    component: <LazyWrapper Component={Tracking} />,
    elements: [
      {
        component: <LazyWrapper Component={Zones} />,
        path: 'monitoreo/zonas',
        title: 'Zonas',
        ElementIcon: PlaceIcon
      },
      {
        component: <LazyWrapper Component={ZoneDetail} />,
        path: 'monitoreo/zona',
        title: 'Zonas',
        ElementIcon: PlaceIcon,
        hide: true
      },
      {
        component: <LazyWrapper Component={Workers} />,
        path: 'monitoreo/colaboradores',
        title: 'Colaboradores',
        ElementIcon: EngineeringIcon
      },
      {
        component: <LazyWrapper Component={WorkerDetail} />,
        path: 'monitoreo/colaborador',
        title: 'Colaboradores',
        ElementIcon: EngineeringIcon,
        hide: true
      },
      {
        component: <LazyWrapper Component={LiveMap} />,
        path: 'monitoreo/mapa',
        title: 'Mapa',
        ElementIcon: MapIcon
      },
      {
        component: <LazyWrapper Component={Machines} />,
        path: 'monitoreo/maquinas',
        title: 'Máquinas',
        ElementIcon: PrecisionManufacturingIcon
      },
      {
        component: <LazyWrapper Component={Machine} />,
        path: 'monitoreo/maquina',
        hide: true,
        title: 'Máquina',
        ElementIcon: SensorsIcon
      }
    ]
  },
  {
    title: 'Estadísticas',
    Icon: InsightsIcon,
    path: 'estadisticas',
    component: <LazyWrapper Component={ReportsMenu} />,
    elements: [
      {
        component: <LazyWrapper Component={Reports} />,
        path: 'estadisticas/reportes',
        title: 'Reportes',
        ElementIcon: SummarizeIcon
      },
      {
        component: <LazyWrapper Component={ReportDetail} />,
        path: 'estadisticas/reporte',
        title: 'Reporte',
        hide: true,
        ElementIcon: SummarizeIcon
      },
      {
        component: <LazyWrapper Component={HeatMap} />,
        path: 'estadisticas/heatmap',
        title: 'Localización Diaria',
        ElementIcon: TimelineIcon
      }
    ]
  },
  {
    title: 'Administrador',
    Icon: AdminPanelSettingsIcon,
    path: 'administrador',
    component: <LazyWrapper Component={AdminMenu} />,
    getProtection: () => {
      const user = UserAPI.getCurrentUser();
      return user.role === 'ADMIN';
    },
    elements: [
      {
        component: <LazyWrapper Component={Users} />,
        path: 'administrador/usuarios',
        title: 'Usuarios',
        ElementIcon: PeopleAltIcon
      },
      {
        component: <LazyWrapper Component={CreateUser} />,
        path: 'administrador/usuario/crear',
        title: 'Crear Usuario',
        ElementIcon: PersonAddIcon
      },
      {
        component: <LazyWrapper Component={User} />,
        path: 'administrador/usuario',
        title: 'Usuario',
        hide: true,
        ElementIcon: PersonIcon
      },
      {
        component: <LazyWrapper Component={Workers} />,
        path: 'administrador/trabajadores',
        title: 'Colaboradores',
        ElementIcon: EngineeringIcon
      },
      {
        component: <LazyWrapper Component={Notices} />,
        path: 'administrador/avisos',
        title: 'Avisos',
        ElementIcon: CampaignIcon
      },
      {
        component: <LazyWrapper Component={CreateNotice} />,
        path: 'administrador/avisos/crear',
        title: 'Crear Usuario',
        hide: true,
        ElementIcon: PersonAddIcon
      }
    ]
  },
  {
    title: 'Ayuda',
    Icon: LiveHelpIcon,
    path: 'ayuda',
    component: <LazyWrapper Component={HelpMenu} />,
    elements: [
      {
        component: <LazyWrapper Component={FAQ} />,
        path: 'ayuda/faq',
        title: 'Preguntas Frecuentes',
        ElementIcon: QuestionAnswerIcon
      }
    ]
  },
  {
    title: 'Mi Usuario',
    Icon: AccountBoxIcon,
    path: 'perfil',
    component: <LazyWrapper Component={MyUserMenu} />,
    elements: [
      {
        component: <LazyWrapper Component={Profile} />,
        path: 'perfil/usuario',
        title: 'Perfil',
        ElementIcon: PermContactCalendarIcon
      },
      {
        component: <LazyWrapper Component={Accesibility} />,
        path: 'perfil/accesibilidad',
        title: 'Accesibilidad',
        ElementIcon: AccessibilityIcon
      },
      {
        component: <LazyWrapper Component={Logout} />,
        path: 'logout',
        title: 'Cerrar Sesión',
        ElementIcon: LogoutIcon
      }
    ]
  }
];

export default appRoutesLazy;
